import React, { useEffect, useRef } from 'react';
import { jsPDF } from 'jspdf';
import {
  Bold as BoldIcon,
  Italic as ItalicIcon,
  Underline as UnderlineIcon,
  List,
  Save,
  FileText,
  Download,
  ChevronDown
} from 'lucide-react';

interface TreatmentPlanPDFViewerProps {
  title?: string;
  currentStep?: 'initial' | 'diagnosis' | 'criteria' | 'generation' | 'complete';
  showExportButton?: boolean;
}

const TreatmentPlanPDFViewer: React.FC<TreatmentPlanPDFViewerProps> = ({ 
  title = 'Treatment Plan - 7/31/2025',
  currentStep = 'complete',
  showExportButton = true
}) => {
  const [showExportMenu, setShowExportMenu] = React.useState(false);
  const [pdfGenerated, setPdfGenerated] = React.useState(false);
  const pdfRef = useRef<jsPDF | null>(null);

  // Regenerate PDF when step changes
  useEffect(() => {
    generatePDFContent();
  }, [currentStep]);

  const generatePDFContent = async () => {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = 210;
      const pageHeight = 297;
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;

      // Load images
      const loadImage = (src: string): Promise<HTMLImageElement> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = src;
        });
      };

      try {
        const logoImg = await loadImage('/logo.png');
        const crookImg = await loadImage('/crook.png');

        // Add OrthoMind logo header
        const logoWidth = 20;
        const logoHeight = 20;
        const logoX = (pageWidth - logoWidth) / 2;
        pdf.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 5;

        // Add title
        pdf.setFontSize(24);
        pdf.setTextColor(30, 41, 59);
        pdf.text('Orthodontic Treatment Plan', pageWidth / 2, yPosition, { align: 'center' });
        yPosition += 12;

        // Add date and ID
        pdf.setFontSize(14);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Date: 7/31/2025', pageWidth / 2 - 25, yPosition, { align: 'center' });
        pdf.text('ID: OIM5T3MG', pageWidth / 2 + 25, yPosition, { align: 'center' });
        yPosition += 20;

        // Only add diagnosis if we're at least at the diagnosis step
        if (currentStep === 'initial') {
          // If we're before diagnosis, just show the header
          pdf.setFontSize(14);
          pdf.setTextColor(100, 116, 139);
          pdf.text('Waiting for patient scan upload...', pageWidth / 2, yPosition + 20, { align: 'center' });
        } else if (currentStep === 'diagnosis' || currentStep === 'criteria' || currentStep === 'generation' || currentStep === 'complete') {
          // Add diagnosis section (left side)
          pdf.setFontSize(16);
          pdf.setTextColor(30, 41, 59);
          pdf.text('Diagnosis', margin, yPosition + 5);
          
          pdf.setFontSize(12);
          pdf.setTextColor(51, 65, 85);
          const diagnoses = [
            '• Anterior upper crowding',
            '• Rotation of tooth #22 (upper left lateral incisor)',
            '• Rotation of tooth #23 (upper left canine)',
            '• Maxillary transverse constriction',
            '• Missing lower left lateral incisor (#32)'
          ];
          
          let diagnosisY = yPosition + 12;
          diagnoses.forEach(diagnosis => {
            pdf.text(diagnosis, margin, diagnosisY);
            diagnosisY += 6;
          });
          
          // Add patient scan image (right side)
          const scanWidth = 50;
          const scanHeight = 40;
          const scanX = pageWidth - margin - scanWidth;
          pdf.addImage(crookImg, 'PNG', scanX, yPosition, scanWidth, scanHeight);

          yPosition = Math.max(yPosition + scanHeight + 10, diagnosisY + 10);
        }

        // Only add treatment plan if we're at generation or complete step
        if (currentStep === 'generation' || currentStep === 'complete') {
          // Add Invisalign Treatment Plan
          pdf.setFontSize(14);
          pdf.setTextColor(30, 41, 59);
          pdf.text('Invisalign Treatment Plan', margin, yPosition);
          yPosition += 8;

          pdf.setFontSize(10);
          pdf.setTextColor(51, 65, 85);
          const treatmentSteps = [
            '1. Apply a hinge-like movement to tooth #22, pushing the mesial aspect buccally with a',
            '   fulcrum at the distal.',
            '2. Simultaneously perform transverse expansion of the upper arch.',
            '3. Simultaneously apply distal rotation to the upper molars.',
            '4. Add 10 degrees of buccal root torque to these teeth.',
            '5. Place all attachments on aligner #3.',
            '6. Do not use Power Ridges under any circumstance. Do not perform IPR.',
            '7. At the end of the active aligners, add 4 pairs of passive aligners.'
          ];

          treatmentSteps.forEach(step => {
            // Check if we need a new page
            if (yPosition > pageHeight - margin - 20) {
              pdf.addPage();
              yPosition = margin;
              
              // Add header on new page
              pdf.setFontSize(8);
              pdf.setTextColor(128, 128, 128);
              pdf.text('OrthoMind - Orthodontic Treatment Plan', margin, margin - 5);
              pdf.setDrawColor(200, 200, 200);
              pdf.line(margin, margin - 2, pageWidth - margin, margin - 2);
              yPosition = margin + 5;
            }
            
            const lines = pdf.splitTextToSize(step, contentWidth);
            pdf.text(lines, margin, yPosition);
            yPosition += lines.length * 4;
          });
        } else if (currentStep === 'criteria') {
          // Show status for criteria gathering
          pdf.setFontSize(12);
          pdf.setTextColor(100, 116, 139);
          pdf.text('Gathering treatment criteria and patient information...', pageWidth / 2, yPosition + 10, { align: 'center' });
        }

        // Add footer
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, pageHeight - 25, pageWidth - margin, pageHeight - 25);
        
        pdf.setFontSize(9);
        pdf.setTextColor(128, 128, 128);
        pdf.text('Page 1', margin, pageHeight - 15);
        pdf.text('Exported with OrthoMind', pageWidth - margin - 40, pageHeight - 15);
        pdf.text(new Date().toLocaleDateString(), pageWidth / 2, pageHeight - 15, { align: 'center' });

        // Add small logo at bottom
        pdf.addImage(logoImg, 'PNG', pageWidth / 2 - 5, pageHeight - 40, 10, 10);

        // Store PDF for download
        pdfRef.current = pdf;
        setPdfGenerated(true);

      } catch (error) {
        console.error('Error generating PDF:', error);
      }
    };

  const handleDownloadPDF = () => {
    if (pdfRef.current) {
      pdfRef.current.save('treatment-plan.pdf');
    }
    setShowExportMenu(false);
  };

  // Function to determine what content to show based on current step
  const getContentForStep = () => {
    switch (currentStep) {
      case 'diagnosis':
        return {
          showTreatmentPlan: false,
          statusMessage: 'Analyzing scan and generating diagnosis...'
        };
      case 'criteria':
        return {
          showTreatmentPlan: false,
          statusMessage: 'Gathering treatment criteria...'
        };
      case 'generation':
        return {
          showTreatmentPlan: true,
          statusMessage: null // Remove the status message during generation
        };
      case 'complete':
        return {
          showTreatmentPlan: true,
          statusMessage: null
        };
      default:
        return {
          showTreatmentPlan: false,
          statusMessage: 'Waiting for patient scan upload...'
        };
    }
  };

  const { showTreatmentPlan, statusMessage } = getContentForStep();

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Editor-like Header */}
      <div className="border-b border-medical-200 p-3 sm:p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 sm:mb-4 space-y-2 sm:space-y-0">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-medical-600" />
            <input
              type="text"
              value={title}
              className="text-base sm:text-lg font-medium bg-transparent border-none outline-none focus:ring-0 w-full sm:w-auto cursor-default"
              readOnly
            />
          </div>

          <div className="flex items-center space-x-2">
            <button
              className="medical-button-secondary px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base flex items-center space-x-1 sm:space-x-2"
              disabled
            >
              <Save className="w-3 h-3 sm:w-4 sm:h-4" />
              <span>Save</span>
            </button>

            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="medical-button-secondary px-3 sm:px-4 py-1.5 sm:py-2 text-sm sm:text-base flex items-center space-x-1 sm:space-x-2"
              >
                <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                <span>Export</span>
                <ChevronDown className="w-2 h-2 sm:w-3 sm:h-3" />
              </button>
              
              {showExportMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-medical-200 z-50">
                  <button
                    onClick={handleDownloadPDF}
                    className="w-full text-left px-4 py-2 hover:bg-medical-50 transition-colors flex items-center space-x-2"
                  >
                    <FileText className="w-4 h-4 text-red-600" />
                    <span>Download PDF</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toolbar (disabled for appearance) */}
        <div className="flex items-center space-x-1 p-1.5 sm:p-2 bg-medical-50 rounded-lg overflow-x-auto">
          <button className="p-2 rounded text-medical-400 cursor-not-allowed" disabled>
            <BoldIcon className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          <button className="p-2 rounded text-medical-400 cursor-not-allowed" disabled>
            <ItalicIcon className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          <button className="p-2 rounded text-medical-400 cursor-not-allowed" disabled>
            <UnderlineIcon className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
          <div className="w-px h-6 bg-medical-300 mx-2" />
          <button className="p-2 rounded text-medical-400 cursor-not-allowed" disabled>
            <List className="w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>

      </div>

      {/* PDF Preview Area */}
      <div className="flex-1 overflow-auto bg-gray-100">
        <div className="min-h-full bg-white max-w-4xl mx-auto shadow-lg">
          <div className="p-8">
              {/* Visual representation of the PDF content */}
              <div className="text-center mb-10">
                <img src="/logo.png" alt="OrthoMind" className="w-20 h-20 mx-auto mb-6" />
                <h1 className="text-4xl font-semibold text-medical-900 mb-3">Orthodontic Treatment Plan</h1>
                <div className="text-lg text-medical-600">
                  <span>Date: {new Date().toLocaleDateString()}</span>
                  <span className="mx-4">|</span>
                  <span>ID: OIM5T3MG</span>
                </div>
              </div>

              {/* Status message during generation */}
              {statusMessage && (
                <div className="bg-medical-50 border border-medical-200 rounded-lg p-4 mb-8 text-center">
                  <div className="flex items-center justify-center space-x-3">
                    {currentStep !== 'initial' && currentStep !== 'complete' && (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-medical-600"></div>
                    )}
                    <p className="text-medical-700">{statusMessage}</p>
                  </div>
                </div>
              )}

              {(currentStep === 'diagnosis' || currentStep === 'criteria' || currentStep === 'generation' || currentStep === 'complete') && (
                <div className="flex items-start gap-8 mb-10">
                  <div className="flex-1">
                    <h2 className="text-2xl font-semibold text-medical-900 mb-4">Diagnosis</h2>
                    <ul className="text-lg text-medical-700 space-y-2">
                      <li>• Anterior upper crowding</li>
                      <li>• Rotation of tooth #22 (upper left lateral incisor)</li>
                      <li>• Rotation of tooth #23 (upper left canine)</li>
                      <li>• Maxillary transverse constriction</li>
                      <li>• Missing lower left lateral incisor (#32)</li>
                    </ul>
                  </div>
                  <img src="/crook.png" alt="Patient Scan" className="w-60 h-auto rounded-lg shadow-sm" />
                </div>
              )}

              {showTreatmentPlan && (
                <div>
                  <h2 className="text-2xl font-semibold text-medical-900 mb-6">Invisalign Treatment Plan</h2>
                  <ol className="text-lg text-medical-700 space-y-3">
                    <li>1. Apply a hinge-like movement to tooth #22, pushing the mesial aspect buccally with a fulcrum at the distal.</li>
                    <li>2. Simultaneously perform transverse expansion of the upper arch.</li>
                    <li>3. Simultaneously apply distal rotation to the upper molars.</li>
                    <li>4. Add 10 degrees of buccal root torque to these teeth.</li>
                    <li>5. Place all attachments on aligner #3.</li>
                    <li>6. Do not use Power Ridges under any circumstance. Do not perform IPR.</li>
                    <li>7. At the end of the active aligners, add 4 pairs of passive aligners.</li>
                  </ol>
                </div>
              )}
              
              {pdfGenerated && currentStep === 'complete' && (
                <div className="mt-10 text-center text-base text-medical-500">
                  <p>Treatment plan complete! Click "Export" → "Download PDF" to save.</p>
                </div>
              )}
              
              {/* Footer area with logo */}
              <div className="mt-20 pt-20 border-t border-medical-200">
                <div className="text-center">
                  <img src="/logo.png" alt="OrthoMind" className="w-16 h-16 mx-auto opacity-60" />
                  <p className="text-sm text-medical-400 mt-3">Generated by OrthoMind AI</p>
                </div>
              </div>
              
              {/* Extra padding at bottom */}
              <div className="h-24"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentPlanPDFViewer;